// =====================================================
// WORD COUNTS of a comparison, taken once from its finished result.
//
// Until stage 6 of the engine cleanup the counts were a running tally: the word diff added one for
// every diff piece (so a comma inserted after an unchanged word counted as a word inserted, and a
// word edited inside counted twice or more), and the split, merge and move passes added and
// subtracted their own counts as they re-typed entries. Now nothing counts while the comparison is
// made; countWords reads the result once.
//
// - A word is a run of characters between spaces with a letter, a digit or a symbol in it (an emoji,
//   a ©): "Seller", "2.1", "$500". Punctuation standing alone, a dash, a bullet, a section sign or a
//   table's cell separator, is not a word.
// - A word of the original is deleted when one of those characters of it is deleted; a word of the
//   revised draft is inserted when one of its is inserted. A replaced word counts once deleted and
//   once inserted, as the summary page says. Punctuation alone changes no count.
// - Moved words are neither: a move counts once (moves), whatever its size.
// - Unchanged: the revised draft's words with nothing of theirs inserted or moved.
// - Restructured: each paragraph mark a split or merge inserts or deletes.
// =====================================================

import type { DiffParagraph, DiffStats } from '../types';
import { wordOf } from './word-index';

/** A text's tokens, as the word diff splits it (diff-engine-core tokenize). */
const tokenize = (text: string) => text.match(/\S+/g) || [];

const IS_WORD = /[\p{L}\p{N}\p{So}]/u;
/** The words of a text (tokens with a letter, a digit or a symbol). */
const wordCount = (text: string | undefined) => tokenize(text || '').filter(t => IS_WORD.test(t)).length;

export function countWords(paragraphs: DiffParagraph[]): DiffStats {
  const stats: DiffStats = { deletions: 0, additions: 0, unchanged: 0, moves: 0, restructured: 0 };
  const moves = new Set<number>();
  for (const p of paragraphs) {
    if (!p || !p.type || (p as { _freeTierDivider?: boolean })._freeTierDivider) continue;   // (the free tier's divider)
    if (p.paraMark) stats.restructured!++;
    if (p.moveId != null && (p.type === 'move-from' || p.type === 'move-to')) moves.add(p.moveId);
    if (p.type === 'equal') { stats.unchanged += wordCount(p.revRich?.text ?? p.text); continue; }
    if (p.type === 'para-delete') { stats.deletions += wordCount(p.origRich?.text ?? p.text); continue; }
    if (p.type === 'para-insert') { stats.additions += wordCount(p.revRich?.text ?? p.text); continue; }
    if (!p.diff) continue;   // a paragraph moved whole: its words are moved
    // a word diff: an edited paragraph, or a moved one edited in transit
    const origWords = tokenize(p.origRich?.text || ''), revWords = tokenize(p.revRich?.text || '');
    const deleted = new Set<number>(), inserted = new Set<number>(), moved = new Set<number>(), seen = new Set<number>();
    for (const d of p.diff) {
      if (d.moveId != null) moves.add(d.moveId);
      const changes = IS_WORD.test(d.value);
      if (d.type === 'delete') { if (changes) deleted.add(wordOf(d, 'orig')); }
      else if (d.type === 'insert') { const w = wordOf(d, 'rev'); if (changes) inserted.add(w); seen.add(w); }
      else if (d.type === 'move-to') { const w = wordOf(d, 'rev'); moved.add(w); seen.add(w); }
      else if (d.type === 'equal') seen.add(wordOf(d, 'rev'));
    }
    for (const w of deleted) if (IS_WORD.test(origWords[w] || '')) stats.deletions++;
    for (const w of inserted) if (!moved.has(w) && IS_WORD.test(revWords[w] || '')) stats.additions++;
    // a moved paragraph's words are moved, edited or not
    if (p.type === 'move-to') continue;
    for (const w of seen) if (!inserted.has(w) && !moved.has(w) && IS_WORD.test(revWords[w] || '')) stats.unchanged++;
  }
  stats.moves = moves.size;
  return stats;
}

/** True when the comparison changed the body: a paragraph inserted, deleted or moved (a blank line
 *  too), a paragraph split or merged, or an edit that changes more than spacing. The word counts can
 *  be zero for every one of these (a blank line has no words, punctuation is not a word), so the
 *  no-changes notice asks this instead (renderer). Formatting alone is not a change here: the text
 *  is the same. */
export function bodyChanged(paragraphs: DiffParagraph[]): boolean {
  return paragraphs.some(p => {
    if (!p || (p as { _freeTierDivider?: boolean })._freeTierDivider) return false;
    if (p.paraMark) return true;
    if (p.type === 'equal') return false;
    if (p.type === 'modify' && p.diff) return p.diff.some(d => d.type !== 'equal' && !!d.value.trim());
    return true;
  });
}
